import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import {
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExportButton from 'src/components/export-button';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useGetAppetitesGroupedByImpact, useGetRisksFlat } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';
import { useRiskScores } from '@/hooks/useRiskScore';
import { getCounter } from '@/utils/collectionUtils';
import { addRiskUrl } from '@/utils/urls';

import AssessmentResultModal from '../assessments/modals/AssessmentResultModal';
import { useGetCollectionTableProps } from './config';
import { RiskRegisterToolbar } from './RiskRegisterToolbar';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';

const Page: FC = () => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'risks',
  });
  const { t } = useTranslation(['common'], {});
  const title = st('register_title');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const { data, loading, refetch } = useGetRisksFlat({
    queryArgs: {},
  });
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [openRatingResultId, setOpenRatingResultId] = useState<
    string | undefined
  >();
  const handleRatingResultModalClose = () => {
    setOpenRatingResultId(undefined);
    setIsEditOpen(false);
    refetch();
  };
  const { loading: loadingScores, scores } = useRiskScores();

  const { data: impactAppetites, loading: loadingImpactAppetites } = useGetAppetitesGroupedByImpact({ queryArgs: {} });

  const tableProps = useGetCollectionTableProps(
    data?.risk,
    scores,
    impactAppetites?.impact,
    (ratingId) => {
      setOpenRatingResultId(ratingId);
      setIsEditOpen(true);
    }
  );
  const counter = getCounter(tableProps.totalItemsCount, loading);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  return (
    <PageLayout
      helpTranslationKey={'risks.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-152) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <RiskRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
          <Permission
            permission={'insert:risk'}
            canHaveAccessAsContributor={true}
          >
            <Button variant={'primary'} href={addRiskUrl()}>
              {st('create_new_button')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading || loadingScores || loadingImpactAppetites}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.Risk}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table
        {...tableProps}
        loading={loading || loadingScores || loadingImpactAppetites}
      />
      {isEditOpen && (
        <AssessmentResultModal
          id={openRatingResultId}
          onDismiss={handleRatingResultModalClose}
          resultType={Parent_Type_Enum.RiskAssessmentResult}
          hideTypeSelector={true}
          i18n={t('ratings')}
          assessmentMode={'rating'}
        />
      )}
    </PageLayout>
  );
};

export default Page;
